import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import Button from "../../../../components/ui/Button";
import AddButton from "../../../../components/ui/AddButton";
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import DeleteModal from "../../../../components/modals/DeleteModal";
import {
    getProgressionConfig,
    getLevels,
    deleteLevel,
    updateLevelStatus,
    LevelResponse,
} from "../../../../api/progression";
import i18n from "../../../../i18n";
import styles from "./ManageLevels.module.css";
import Icon from "../../../../components/common/Icon";
import stepStyles from "../createStep.module.css";
import pageHeader from "../../../../components/layouts/PageHeader.module.css";

interface ManageLevelsProps {
    navigateToNext: () => void;
}

const ManageLevels: React.FC<ManageLevelsProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || "";

    const [levels, setLevels] = useState<LevelResponse[]>([]);
    const [progressionConfigId, setProgressionConfigId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [levelToDelete, setLevelToDelete] = useState<LevelResponse | null>(null);

    const linkId = trackData?.trackId || trackData?.id;

    const loadLevels = async () => {
        if (!trackData.trackId) return;

        try {
            setLoading(true);
            const configResponse = await getProgressionConfig({ token, trackId: trackData.trackId });
            if (configResponse?.progressionConfig) {
                setProgressionConfigId(configResponse.progressionConfig.id);
                const levelsResponse = await getLevels({
                    token,
                    progressionConfigId: configResponse.progressionConfig.id,
                });
                if (levelsResponse?.levels) {
                    setLevels(levelsResponse.levels.sort((a, b) => a.position - b.position));
                }
            }
        } catch (error) {
            console.error("Error loading levels:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLevels();
        // fetch-on-mount when track changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId, token]);

    const handleToggleStatus = async (levelId: string, currentStatus: boolean) => {
        // Optimistic update
        setLevels((prev) => prev.map((level) => (level.id === levelId ? { ...level, status: !currentStatus } : level)));
        try {
            await updateLevelStatus({
                token,
                levelId,
                status: !currentStatus,
            });
        } catch (error) {
            console.error("Error updating level status:", error);
            // Revert on error
            setLevels((prev) =>
                prev.map((level) => (level.id === levelId ? { ...level, status: currentStatus } : level)),
            );
        }
    };

    const handleDelete = async () => {
        if (!levelToDelete) return;

        // Optimistic update
        const deletedLevel = levelToDelete;
        setLevels((prev) => prev.filter((level) => level.id !== deletedLevel.id));
        setDeleteModalOpen(false);
        setLevelToDelete(null);

        try {
            await deleteLevel({ token, levelId: deletedLevel.id });
        } catch (error) {
            console.error("Error deleting level:", error);
            // Revert on error
            setLevels((prev) => [...prev, deletedLevel].sort((a, b) => a.position - b.position));
        }
    };

    const handleEdit = (levelId: string) => {
        navigate(`/create/tour/${linkId}/levels/modify/${levelId}`);
    };

    const handleCreate = () => {
        navigate(`/create/tour/${linkId}/levels/create`);
    };

    const getLocalizedName = (level: LevelResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = level.levelLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.name || level.levelLocalized?.[0]?.name || t("level") + " " + level.position;
    };

    const getUnlockDescription = (level: LevelResponse) => {
        // For image scan tracks, use discoveredWaypointsThreshold (objects discovered)
        const count = level.discoveredWaypointsThreshold;
        const key = count <= 1 ? "unlocksAfterObjectsDiscoveredSingular" : "unlocksAfterObjectsDiscoveredPlural";
        return t(key, { count });
    };

    return (
        <div className={styles.container}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
                <Link to={`/create/tour/${linkId}/avatar`}>{t("progression")}</Link>
                <span>›</span>
                <span style={{ color: "var(--green-600)" }}>{t("levels")}</span>
            </div>

            {/* Header */}
            <div className={pageHeader.withBreadcrumb}>
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className="settings-title">{t("createAndManageLevels")}</h2>
                        <p className={pageHeader.subtitle}>{t("levelsDefineMainProgression")}</p>
                    </div>
                    <div className={styles.headerActions}>
                        <AddButton onClick={handleCreate} tooltip={t("addNewLevel")} disabled={!progressionConfigId} />
                    </div>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className={styles.loading}>{t("loading")}</div>
            ) : levels.length === 0 ? (
                <div className={styles.emptyState}>
                    <h3 className={styles.emptyStateTitle}>{t("noLevelsYet")}</h3>
                    <p className={styles.emptyStateText}>{t("noLevelsDescription")}</p>
                    <AddButton onClick={handleCreate} tooltip={t("createFirstLevel")} disabled={!progressionConfigId} />
                </div>
            ) : (
                <div className={styles.levelsList}>
                    {levels.map((level) => {
                        return (
                            <div
                                key={level.id}
                                className={`${styles.levelCard} ${!level.status ? styles.levelCardInactive : ""}`}
                                onClick={() => handleEdit(level.id)}
                            >
                                {/* Level image or badge */}
                                {level.imageUrl ? (
                                    <div className={styles.levelImageContainer}>
                                        <img
                                            src={level.imageUrl}
                                            alt={getLocalizedName(level)}
                                            className={styles.levelImage}
                                        />
                                        <div className={styles.levelBadgeOverlay}>{level.position}</div>
                                    </div>
                                ) : (
                                    <div className={styles.levelBadge}>
                                        {level.position}
                                        <div className={styles.levelBadgeIcon}>
                                            <svg
                                                width="10"
                                                height="10"
                                                viewBox="0 0 10 10"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <circle cx="5" cy="5" r="3" fill="#ff9500" />
                                            </svg>
                                        </div>
                                    </div>
                                )}

                                {/* Level info */}
                                <div className={styles.levelInfo}>
                                    <h4 className={styles.levelName}>{getLocalizedName(level)}</h4>
                                    <p className={styles.levelDescription}>{getUnlockDescription(level)}</p>
                                    <div className={styles.levelMeta}>
                                        <span className={styles.metaTag}>
                                            {t("level")} {level.position}
                                        </span>
                                    </div>
                                </div>

                                {/* Level actions */}
                                <div className={styles.levelActions}>
                                    <ToggleSwitch
                                        checked={level.status}
                                        onChange={() => handleToggleStatus(level.id, level.status)}
                                    />
                                    <div className={styles.actionButtons}>
                                        <Tooltip title={t("edit")} arrow>
                                            <button
                                                type="button"
                                                className={styles.editBtn}
                                                aria-label={t("edit")}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEdit(level.id);
                                                }}
                                            >
                                                <Icon name="ic-edit" size={20} />
                                            </button>
                                        </Tooltip>
                                        <Tooltip title={t("delete")} arrow>
                                            <button
                                                type="button"
                                                className={styles.deleteBtn}
                                                aria-label={t("delete")}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setLevelToDelete(level);
                                                    setDeleteModalOpen(true);
                                                }}
                                            >
                                                <Icon name="ic-trash" size={20} />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Continue — mock .foot */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("continue")}
                    onClick={navigateToNext}
                    disabled={levels.length === 0}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>

            {/* Delete confirmation modal */}
            {levelToDelete && (
                <DeleteModal
                    isOpen={deleteModalOpen}
                    onClose={() => {
                        setDeleteModalOpen(false);
                        setLevelToDelete(null);
                    }}
                    onConfirm={handleDelete}
                    message={t("deleteLevelConfirmation")}
                    title={t("deleteLevel")}
                />
            )}
        </div>
    );
};

export default ManageLevels;
